import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Collapse,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Stack,
} from '@mui/material';
import { api, ensureUiScope, persistUiScope } from '../services/api';
import { camerasApi, LiveViewReadiness } from '../services/camerasApi';
import {
  changeOwnPassword,
  disableOwnMfa,
  fetchOwnMfaStatus,
  startOwnMfaSetup,
  verifyOwnMfaSetup,
} from '../services/usersApi';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HelpOutline as UnknownIcon,
  Password as PasswordIcon,
  VerifiedUser as MfaIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { canUsePersonaPreviewControls, getCapabilitiesFromUser, hasCapability, isExternalSafetyReviewer, isProductEvaluator, isSandboxEhsEvaluator } from '../auth/access';
import { PERSONA_PRESETS, getPersonaPresetById, type PersonaPresetId } from '../auth/personaPresets';
import { isPersonaPreviewFeatureEnabled } from '../auth/previewMode';
import RouteRenderBoundary from './RouteRenderBoundary';
import ProductEvaluatorScopeBootstrap from './ProductEvaluatorScopeBootstrap';
import { mergeNavContext, readNavContext, writeNavContext, type NavContext } from '../utils/navContext';
import { buildScopedIncidentsPath } from '../utils/incidentsNavigation';
import {
  resolveWarehouseLandingPath,
  resolveWarehouseNavigation,
  type WarehouseSectionKey,
} from '../utils/warehouseNavigation';
import { resolveIdentityTransportBadgeState } from '../domain/identityTransportBadge';
import {
  cameraViewStaleLabel,
  chipToneAfterRefresh,
  incidentPipelineChipLabel,
} from './incidentPipelineHealth';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'ui.sidebar.collapsed';

function readSidebarCollapsedPreference(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persistSidebarCollapsedPreference(collapsed: boolean): void {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // ignore quota / private mode
  }
}

const NAV_MACRO_GROUPS: Array<{ label: string; sectionKeys: WarehouseSectionKey[] }> = [
  { label: 'Operations', sectionKeys: ['impact', 'overview', 'liveOps', 'acceptance'] },
  { label: 'Configuration', sectionKeys: ['setupRollout'] },
  { label: 'Administration', sectionKeys: ['access'] },
];

const PERSONA_PREVIEW_GROUPS: Array<{ label: string; presetIds: PersonaPresetId[] }> = [
  { label: 'General', presetIds: ['viewer', 'live_ops'] },
  { label: 'Setup & acceptance', presetIds: ['setup_reader', 'setup_writer', 'acceptance_manager'] },
  { label: 'Administration', presetIds: ['access_admin', 'super_admin'] },
];

function readUrlNavContext(href: string): NavContext {
  try {
    const url = new URL(href, window.location.origin);
    const p = url.pathname || '';
    const qp = url.searchParams;
    const next: NavContext = {};
    const deviceFromQuery = (qp.get('device') || qp.get('device_id') || '').trim();
    const cameraFromQuery = (qp.get('camera_id') || '').trim();
    const siteFromQuery = (qp.get('site') || qp.get('site_id') || '').trim();

    if (deviceFromQuery) next.device_id = deviceFromQuery;
    if (cameraFromQuery) next.camera_id = cameraFromQuery;
    if (siteFromQuery) next.site_id = siteFromQuery;

    // Infer from paths like /cameras/:cameraId and /devices/:deviceId/control
    const camMatch = p.match(/^\/cameras\/([^/]+)$/);
    if (camMatch?.[1]) next.camera_id = decodeURIComponent(camMatch[1]);
    const devCtrlMatch = p.match(/^\/devices\/([^/]+)\/control/);
    if (devCtrlMatch?.[1]) next.device_id = decodeURIComponent(devCtrlMatch[1]);

    return next;
  } catch {
    return {};
  }
}

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsedPreference);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [authNote, setAuthNote] = useState<string | null>(null);
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [scopeOrgDraft, setScopeOrgDraft] = useState('');
  const [scopeSiteDraft, setScopeSiteDraft] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [confirmNextPassword, setConfirmNextPassword] = useState('');
  const [mfaCurrentPassword, setMfaCurrentPassword] = useState('');
  const [mfaOtpCode, setMfaOtpCode] = useState('');
  const [mfaStatusLoading, setMfaStatusLoading] = useState(false);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaPendingSetup, setMfaPendingSetup] = useState(false);
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaProvisioningUri, setMfaProvisioningUri] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaSuccess, setMfaSuccess] = useState<string | null>(null);
  const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState<string | null>(null);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [, setScopeNonce] = useState(0);
  /** Collapsed-by-default advanced nav per warehouse lane (presentation only). */
  const [laneAdvancedOpen, setLaneAdvancedOpen] = useState<Record<string, boolean>>({
    setupRollout: false,
    liveOps: false,
  });
  /** Per-lane submenu expanded (true = show children). Undefined => expanded only when lane is active. */
  const [laneSectionExpanded, setLaneSectionExpanded] = useState<Record<string, boolean>>({});
  const { user, effectiveUser, previewPersonaId, setPreviewPersona, clearPreviewPersona, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  function handleMenuClick(path: string) {
    // Docs are served outside the SPA under /docs/.
    if (path === '/docs' || path.startsWith('/docs/')) {
      window.open(path, '_blank', 'noopener,noreferrer');
      setMobileOpen(false);
      return;
    }
    const nextPath = buildNavPath(path);
    const currentPath = `${location.pathname}${location.search || ''}`;
    if (nextPath === currentPath) {
      setMobileOpen(false);
      return;
    }
    navigate(nextPath);
  }
  const hasControlPlaneApiKey = Boolean(process.env.REACT_APP_CONTROL_PLANE_API_KEY);

  // F-17 (P0) RBAC-lite: route/menu gating only (client-side). Backend enforces critical mutations too.
  const previewFeatureOn = isPersonaPreviewFeatureEnabled();
  const canShowPersonaPreview = Boolean(user && previewFeatureOn && canUsePersonaPreviewControls(user));
  const warehouseSections = useMemo(() => resolveWarehouseNavigation(effectiveUser), [effectiveUser]);
  const activeWarehouseSectionKey = useMemo(() => {
    const p = location.pathname || '';
    if (
      p === '/acceptance/impact' ||
      p.startsWith('/acceptance/impact/') ||
      p.startsWith('/acceptance/case-studies/') ||
      p === '/overview/demo-story'
    ) return 'impact';
    if (
      p === '/acceptance/overview' ||
      p === '/acceptance/overview-v2' ||
      p === '/dev/acceptance/overview-v2' ||
      p.startsWith('/acceptance/overview/') ||
      p === '/overview' ||
      (p.startsWith('/overview/') && p !== '/overview/demo-story')
    ) return 'overview';
    if (
      p === '/live-ops' || p.startsWith('/live-ops/') ||
      p === '/incidents' || p.startsWith('/incidents/') ||
      p === '/live' || p.startsWith('/live/') ||
      p === '/live-view' || p.startsWith('/live-view/') ||
      p === '/analytics' || p.startsWith('/analytics/') ||
      p === '/audit' || p.startsWith('/audit/') ||
      p === '/odoo' || p.startsWith('/odoo/')
    ) return 'liveOps';
    if (
      p === '/setup-rollout' || p.startsWith('/setup-rollout/') ||
      p === '/bringup' || p.startsWith('/bringup/') ||
      p === '/setup' || p.startsWith('/setup/') ||
      p === '/preflight' || p.startsWith('/preflight/') ||
      p === '/devices' || p.startsWith('/devices/') ||
      p === '/cameras' || p.startsWith('/cameras/') ||
      p === '/zones' || p.startsWith('/zones/') ||
      p === '/policies' || p.startsWith('/policies/') ||
      p === '/accuracy' || p.startsWith('/accuracy/') ||
      p === '/blueprints' || p.startsWith('/blueprints/') ||
      p === '/config-snapshots' || p.startsWith('/config-snapshots/') ||
      p === '/agents' || p.startsWith('/agents/') ||
      p === '/configurations' || p.startsWith('/configurations/') ||
      p === '/deployments' || p.startsWith('/deployments/') ||
      p === '/diagnostics' || p.startsWith('/diagnostics/') ||
      p === '/grafana' || p.startsWith('/grafana/') ||
      p === '/ops/runbooks' || p.startsWith('/ops/runbooks/') ||
      p === '/ops/copilot' || p.startsWith('/ops/copilot/') ||
      p === '/ops/dr' || p.startsWith('/ops/dr/')
    ) return 'setupRollout';
    if (
      p === '/acceptance/reports' ||
      p.startsWith('/acceptance/reports/')
    ) return 'acceptance';
    if (
      p === '/acceptance/cases' ||
      p.startsWith('/acceptance/cases/')
    ) return 'acceptance';
    if (
      p === '/acceptance/proof' ||
      p.startsWith('/acceptance/proof/') ||
      p === '/prove' ||
      p.startsWith('/prove/')
    ) return 'acceptance';
    if (p === '/users' || p.startsWith('/users/') || p === '/admin/access-scope' || p.startsWith('/admin/access-scope/')) return 'access';
    return warehouseSections[0]?.key || 'overview';
  }, [location.pathname, warehouseSections]);
  const isMenuSelected = useCallback((itemPath: string): boolean => {
    const p = location.pathname || '';
    const itemPathname = itemPath.split('?')[0] || itemPath;
    const isDevCtrl = /^\/devices\/[^/]+\/control/.test(p) || p === '/devices/control';

    if (itemPathname === '/') return p === '/';
    if (itemPathname === '/devices/control') return isDevCtrl;
    if (itemPathname === '/devices') return p === '/devices';
    if (itemPathname === '/acceptance/overview') {
      return p === '/acceptance/overview' || p === '/acceptance/overview-v2' || p === '/dev/acceptance/overview-v2';
    }

    // Default: exact match or nested route match (e.g. /cameras/:id should select /cameras)
    return p === itemPathname || p.startsWith(itemPathname + '/');
  }, [location.pathname]);
  useEffect(() => {
    setLaneAdvancedOpen((prev) => {
      const next = { ...prev };
      for (const section of warehouseSections) {
        const advancedPages = section.advancedPages || [];
        if (advancedPages.some((page) => isMenuSelected(page.path))) {
          next[section.key] = true;
        }
      }
      return next;
    });
  }, [location.pathname, warehouseSections, isMenuSelected]);

  useEffect(() => {
    setLaneSectionExpanded({});
  }, [location.pathname, location.search]);

  // Mobile temporary Drawer: close on route change. Otherwise `mobileOpen` stays true, the Modal backdrop
  // remains active, and the main surface looks "frozen" until the hamburger toggles again.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Track context from current URL so menu clicks can deep-link with the same camera/device selected.
    try {
      const next = readUrlNavContext(window.location.href);
      if (Object.keys(next).length) {
        const merged = mergeNavContext(readNavContext(), next);
        writeNavContext(merged);
      }
    } catch {
      // ignore
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Show a lightweight, dismissible banner when we auto-fallback from stale JWT → X-API-Key
    // (prevents the "403 forbidden everywhere" brick after a DB reset / token mismatch).
    const onFallback = (e: any) => {
      const mode = e?.detail?.mode;
      if (mode === 'api_key') {
        setAuthNote('Session auth was rejected; switched to API-key mode for this browser.');
      }
    };
    window.addEventListener('edgentik:auth-fallback', onFallback as any);
    return () => window.removeEventListener('edgentik:auth-fallback', onFallback as any);
  }, []);

  const buildNavPath = (basePath: string): string => {
    // Temporary booth shortcut: keep the exact Showcase query string.
    if (basePath === '/showcase' || basePath.startsWith('/showcase?')) return basePath;

    const ctx = readNavContext();
    if (!ctx || (!ctx.device_id && !ctx.camera_id && !ctx.site_id)) return basePath;

    // Special: "Device Control" should deep-link to the last selected device control page when possible.
    if (basePath === '/devices/control') {
      return '/devices/control';
    }

    // Only decorate links where it helps UX and doesn't break routes.
    const url = new URL(basePath, window.location.origin);

    // Keep site context if present (future-proof)
    if (ctx.site_id && !url.searchParams.get('site') && !url.searchParams.get('site_id')) {
      url.searchParams.set('site', ctx.site_id);
    }

    if (basePath === '/preflight') {
      if (ctx.device_id && !url.searchParams.get('device') && !url.searchParams.get('device_id')) {
        url.searchParams.set('device', ctx.device_id);
      }
    }

    if (basePath === '/ops/runbooks') {
      if (ctx.device_id && !url.searchParams.get('device')) {
        url.searchParams.set('device', ctx.device_id);
      }
    }

    if (basePath === '/ops/copilot') {
      if (ctx.device_id && !url.searchParams.get('device')) {
        url.searchParams.set('device', ctx.device_id);
      }
    }

    if (basePath === '/incidents') {
      return buildScopedIncidentsPath(basePath, { navContext: ctx });
    }

    // Important: Live View should default to all site cameras.
    // Do not auto-pin camera_id from sticky nav context, because it hides other cameras
    // (e.g., operator expects frontdoor/backdoor but sees only one stream).

    if (basePath === '/diagnostics') {
      if (ctx.device_id && !url.searchParams.get('device') && !url.searchParams.get('device_id')) {
        url.searchParams.set('device', ctx.device_id);
      }
    }

    if (basePath === '/grafana') {
      // Only scope Grafana from the current route when the current page explicitly carries
      // device/camera context. This avoids stale incident context hijacking metrics.
      const currentUrlCtx = readUrlNavContext(window.location.href);
      if (currentUrlCtx.device_id && !url.searchParams.get('device') && !url.searchParams.get('device_id')) {
        url.searchParams.set('device_id', currentUrlCtx.device_id);
      }
      if (currentUrlCtx.camera_id && !url.searchParams.get('camera_id')) {
        url.searchParams.set('camera_id', currentUrlCtx.camera_id);
      }
    }

    return url.pathname + (url.search ? url.search : '');
  };
  // Integrated incident/evidence health (cloud-side view)
  const [incidentHealth, setIncidentHealth] = useState<any | null>(null);
  const [incidentHealthError, setIncidentHealthError] = useState<string | null>(null);
  const [incidentHealthLoading, setIncidentHealthLoading] = useState(false);
  const [incidentHealthSucceededAt, setIncidentHealthSucceededAt] = useState<string | null>(null);
  const [liveViewReadiness, setLiveViewReadiness] = useState<LiveViewReadiness | null>(null);
  const [liveViewReadinessError, setLiveViewReadinessError] = useState<string | null>(null);
  const [liveViewReadinessLoading, setLiveViewReadinessLoading] = useState(false);
  const [liveViewSucceededAt, setLiveViewSucceededAt] = useState<string | null>(null);
  
  // mTLS identity verification state
  const [identityInfo, setIdentityInfo] = useState<any | null>(null);

  useEffect(() => {
    let mounted = true;
    let timer: any = null;
    
    // Fetch mTLS identity info (once on load)
    const fetchIdentity = async () => {
      try {
        const res = await fetch('/api/v1/identity', {
          headers: { Accept: 'application/json' },
        });
        if (res.ok) {
          const data = await res.json();
          if (mounted) setIdentityInfo(data);
        }
      } catch (e) {
        // Silently ignore - identity endpoint may not be available
      }
    };
    fetchIdentity();

    const persistScopeFromBackend = (orgId: string, siteId: string) => {
      try {
        const userSet = String(localStorage.getItem('ui.scope.user_set') || '').trim() === 'true';
        if (!userSet) {
          const isDefaultPair = orgId === 'default' && siteId === 'default';
          if (orgId && siteId && !isDefaultPair) {
            persistUiScope(orgId, siteId);
            window.dispatchEvent(new CustomEvent('edgentik:scope-changed', { detail: { orgId, siteId } }));
            setScopeNonce((n) => n + 1);
          }
        }
      } catch {
        // ignore
      }
    };

    const fetchScopeBootstrap = async () => {
      try {
        const scope = ensureUiScope();
        if (scope.source !== 'none' || scope.orgId) return;
        const { data } = await api.get<{ ok?: boolean; org_id?: string; site_id?: string }>('/api/v1/scope/bootstrap');
        if (mounted && data?.ok && data.org_id && data.site_id) {
          const orgId = String(data.org_id).trim();
          const siteId = String(data.site_id).trim();
          const isDefaultPair = orgId === 'default' && siteId === 'default';
          if (!isDefaultPair) persistScopeFromBackend(orgId, siteId);
        }
      } catch {
        // ignore - bootstrap is best-effort
      }
    };

    const fetchIncidentHealth = async () => {
      try {
        setIncidentHealthLoading(true);
        const { data } = await api.get('/api/v1/health/incident_e2e');
        // Persist org/site scope for RBAC (best-effort). Used by axios interceptor in services/api.ts.
        try {
          const orgIdRaw = data?.context?.org_id;
          const siteIdRaw = data?.context?.site_id;
          const orgId = typeof orgIdRaw === 'string' ? orgIdRaw.trim() : '';
          const siteId = typeof siteIdRaw === 'string' ? siteIdRaw.trim() : '';
          if (orgId && siteId) persistScopeFromBackend(orgId, siteId);
        } catch {
          // ignore
        }
        if (mounted) {
          setIncidentHealth(data);
          setIncidentHealthError(null);
          setIncidentHealthSucceededAt(
            typeof data?.timestamp === 'string' && data.timestamp.trim()
              ? data.timestamp
              : new Date().toISOString(),
          );
        }
      } catch (e: any) {
        if (mounted) setIncidentHealthError(e?.message || 'Failed to fetch');
      } finally {
        if (mounted) setIncidentHealthLoading(false);
      }
    };

    fetchScopeBootstrap();
    fetchIncidentHealth();
    timer = setInterval(fetchIncidentHealth, 15000);
    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    let timer: ReturnType<typeof setInterval> | null = null;

    const fetchLiveViewReadiness = async () => {
      const scope = ensureUiScope();
      const siteId = String(scope.siteId || '').trim();
      if (!siteId) {
        if (mounted) {
          setLiveViewReadiness(null);
          setLiveViewReadinessError(null);
          setLiveViewReadinessLoading(false);
        }
        return;
      }
      try {
        setLiveViewReadinessLoading(true);
        const data = await camerasApi.getLiveViewReadiness(siteId);
        if (mounted) {
          setLiveViewReadiness(data);
          setLiveViewReadinessError(null);
          setLiveViewSucceededAt(new Date().toISOString());
        }
      } catch (e: any) {
        if (mounted) setLiveViewReadinessError(e?.message || 'Failed to fetch');
      } finally {
        if (mounted) setLiveViewReadinessLoading(false);
      }
    };

    fetchLiveViewReadiness();
    timer = setInterval(fetchLiveViewReadiness, 45000);
    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
    };
  }, []);

  const incidentHealthChip = useMemo(() => {
    const ok = Boolean(incidentHealth?.ok);
    const hasData = incidentHealth !== null;
    const isLoading = incidentHealthLoading && !hasData;

    const checks = incidentHealth?.checks || {};
    const dbOk = checks?.db?.ok;
    const mqttOk = checks?.mqtt?.ok;
    const odooOk = checks?.odoo?.ok;
    const evidenceOk = checks?.evidence_backfill?.ok;
    const advisoryFailingChecks = Object.entries(checks).filter(([, value]) => {
      if (!value || typeof value !== 'object') return false;
      const check = value as Record<string, unknown>;
      return check.blocking === false && check.ok === false;
    });
    const hasAdvisory = Boolean(incidentHealth?.has_advisory) || advisoryFailingChecks.length > 0;

    const tooltipLines: string[] = [
      'Incident pipeline (cloud E2E checks). This does not prove Overview condition-state can load.',
    ];
    if (incidentHealthSucceededAt) tooltipLines.push(`Last success: ${incidentHealthSucceededAt}`);
    if (incidentHealthError) tooltipLines.push(`Error: ${incidentHealthError}`);
    if (hasData) {
      if (typeof dbOk === 'boolean') tooltipLines.push(`DB: ${dbOk ? 'OK' : 'FAIL'}`);
      if (typeof mqttOk === 'boolean') {
        const mqttBlocking = checks?.mqtt?.blocking !== false;
        tooltipLines.push(`Cloud MQTT${mqttBlocking ? '' : ' (advisory)'}: ${mqttOk ? 'OK' : 'FAIL'}`);
      }
      if (typeof odooOk === 'boolean') tooltipLines.push(`Odoo: ${odooOk ? 'OK' : 'FAIL'}`);
      if (typeof evidenceOk === 'boolean') tooltipLines.push(`Evidence backfill: ${evidenceOk ? 'OK' : 'WARN'}`);
      if (advisoryFailingChecks.length > 0) {
        tooltipLines.push(`Advisory checks failing: ${advisoryFailingChecks.map(([name]) => name).join(', ')}`);
      }
      if (incidentHealth?.timestamp) tooltipLines.push(`Updated: ${incidentHealth.timestamp}`);
    }

    const tone = chipToneAfterRefresh({
      hasData,
      loading: isLoading,
      error: incidentHealthError,
      healthy: ok,
      advisory: hasAdvisory,
      lastSuccessAt: incidentHealthSucceededAt,
    });
    let label = incidentPipelineChipLabel(tone, incidentHealthSucceededAt);
    let color: any = 'default';
    let icon: React.ReactNode = <UnknownIcon sx={{ fontSize: 16 }} />;

    if (tone === 'checking') {
      icon = <CircularProgress size={14} color="inherit" />;
    } else if (tone === 'advisory' || tone === 'stale' || tone === 'unknown') {
      color = 'warning';
      icon = <UnknownIcon sx={{ fontSize: 16 }} />;
    } else if (tone === 'healthy') {
      color = 'success';
      icon = <SuccessIcon sx={{ fontSize: 16 }} />;
    } else if (tone === 'degraded') {
      color = 'error';
      icon = <ErrorIcon sx={{ fontSize: 16 }} />;
    }

    const tooltip = (
      <Box sx={{ whiteSpace: 'pre-line', p: 0.5 }}>
        {tooltipLines.length ? tooltipLines.join('\n') : 'No data'}
      </Box>
    );

    const sx = (() => {
      // High-contrast chip colors for the blue app bar
      if (color === 'success') {
        return {
          mr: 1,
          bgcolor: 'success.dark',
          color: 'common.white',
          borderColor: 'success.dark',
          '& .MuiChip-icon': { color: 'common.white' },
        };
      }
      if (color === 'error') {
        return {
          mr: 1,
          bgcolor: 'error.dark',
          color: 'common.white',
          borderColor: 'error.dark',
          '& .MuiChip-icon': { color: 'common.white' },
        };
      }
      if (color === 'warning') {
        return {
          mr: 1,
          bgcolor: 'warning.dark',
          color: 'common.white',
          borderColor: 'warning.dark',
          '& .MuiChip-icon': { color: 'common.white' },
        };
      }
      return {
        mr: 1,
        bgcolor: 'grey.800',
        color: 'common.white',
        borderColor: 'grey.800',
        '& .MuiChip-icon': { color: 'common.white' },
      };
    })();

    return (
      <Tooltip title={tooltip} arrow>
        <Chip
          icon={icon}
          label={label}
          size="small"
          color={color}
          variant="filled"
          sx={sx}
          data-testid="incident-pipeline-health"
          data-health-tone={tone}
        />
      </Tooltip>
    );
  }, [incidentHealth, incidentHealthError, incidentHealthLoading, incidentHealthSucceededAt]);

  const liveViewHealthChip = useMemo(() => {
    const hasData = liveViewReadiness !== null;
    const isLoading = liveViewReadinessLoading && !hasData;
    const onlineDevices = Number(liveViewReadiness?.online_device_count ?? 0);
    const ready = Number(liveViewReadiness?.cameras_live_view_ready ?? 0);
    const waiting = Number(liveViewReadiness?.cameras_waiting ?? 0);

    const tooltipLines: string[] = [
      'Camera view availability (online edge units only)',
      'Camera stream availability is separate from measured site-condition currency.',
    ];
    if (liveViewReadinessError) tooltipLines.push(`Error: ${liveViewReadinessError}`);
    if (liveViewSucceededAt) tooltipLines.push(`Last success: ${liveViewSucceededAt}`);
    if (hasData) {
      tooltipLines.push(`Online devices: ${onlineDevices}`);
      tooltipLines.push(`Camera-view-ready cameras: ${ready}`);
      if (waiting > 0) tooltipLines.push(`Waiting: ${waiting}`);
      const offlineCams = Number(liveViewReadiness?.cameras_on_offline_devices ?? 0);
      if (offlineCams > 0) {
        tooltipLines.push(`Cameras on powered-off units: ${offlineCams} (not gated)`);
      }
    }

    let label = 'Camera view: Unknown';
    let color: 'default' | 'success' | 'warning' | 'error' = 'default';
    let icon: React.ReactNode = <UnknownIcon sx={{ fontSize: 16 }} />;

    const liveTone = chipToneAfterRefresh({
      hasData,
      loading: isLoading,
      error: liveViewReadinessError,
      healthy: ready > 0,
      lastSuccessAt: liveViewSucceededAt,
    });
    if (liveTone === 'stale') {
      label = cameraViewStaleLabel(liveViewSucceededAt);
      color = 'warning';
      icon = <UnknownIcon sx={{ fontSize: 16 }} />;
    } else if (isLoading) {
      label = 'Camera view: Checking…';
      icon = <CircularProgress size={14} color="inherit" />;
    } else if (hasData && !liveViewReadinessError) {
      if (onlineDevices === 0) {
        label = 'Camera view: No online units';
        color = 'default';
      } else if (ready > 0) {
        label = 'Camera view available';
        color = 'success';
        icon = <SuccessIcon sx={{ fontSize: 16 }} />;
      } else if (waiting > 0) {
        label = 'Camera view: Waiting';
        color = 'warning';
        icon = <UnknownIcon sx={{ fontSize: 16 }} />;
      } else {
        label = 'Camera view: Not ready';
        color = 'warning';
        icon = <UnknownIcon sx={{ fontSize: 16 }} />;
      }
    } else if (liveViewReadinessError) {
      label = 'Camera view: Unknown';
      color = 'warning';
    }

    const chipSx =
      color === 'success'
        ? { mr: 1, bgcolor: 'success.dark', color: 'common.white', '& .MuiChip-icon': { color: 'common.white' } }
        : color === 'warning'
          ? { mr: 1, bgcolor: 'warning.dark', color: 'common.white', '& .MuiChip-icon': { color: 'common.white' } }
          : { mr: 1, bgcolor: 'grey.800', color: 'common.white', '& .MuiChip-icon': { color: 'common.white' } };

    return (
      <Tooltip
        title={
          <Box sx={{ whiteSpace: 'pre-line', p: 0.5 }}>
            {tooltipLines.join('\n')}
          </Box>
        }
        arrow
      >
        <Chip icon={icon} label={label} size="small" color={color} variant="filled" sx={chipSx} />
      </Tooltip>
    );
  }, [liveViewReadiness, liveViewReadinessError, liveViewReadinessLoading, liveViewSucceededAt]);

  const siteChip = useMemo(() => {
    const scope = ensureUiScope();
    const ctxSite = String(incidentHealth?.context?.site_id || '').trim();
    const ctxOrg = String(incidentHealth?.context?.org_id || '').trim();
    const siteId = scope.siteId || ctxSite || 'default';
    const orgId = scope.orgId || ctxOrg || 'default';
    const isDefault = !siteId || siteId === 'default';
    const isMultiTenant = orgId && orgId !== 'default';
    const sandbox = isSandboxEhsEvaluator(effectiveUser);
    const baseLabel = isMultiTenant ? `Org: ${orgId} | Site: ${siteId}` : `Site: ${siteId}`;
    const label = sandbox ? `${baseLabel} | Evaluation sandbox` : baseLabel;
    return (
      <Tooltip title={sandbox ? 'WorkSafeBC evaluation sandbox (demo/ws1 only)' : 'Click to change org/site scope'} arrow>
        <Box
          component="span"
          onClick={sandbox ? undefined : () => setScopeDialogOpen(true)}
          sx={{
            display: 'inline-flex',
            cursor: sandbox ? 'default' : 'pointer',
            mr: 1,
          }}
        >
          <Chip
            label={label}
            size="small"
            variant="filled"
            sx={{
              bgcolor: isDefault ? 'warning.main' : 'rgba(255,255,255,0.18)',
              color: 'common.white',
              borderColor: isDefault ? 'warning.dark' : 'rgba(255,255,255,0.25)',
              pointerEvents: 'none',
            }}
          />
        </Box>
      </Tooltip>
    );
  }, [effectiveUser, incidentHealth?.context?.org_id, incidentHealth?.context?.site_id]);

  useEffect(() => {
    if (!scopeDialogOpen) return;
    try {
      const s = ensureUiScope();
      setScopeOrgDraft(String(s.orgId || '').trim());
      setScopeSiteDraft(String(s.siteId || '').trim());
    } catch {
      setScopeOrgDraft('');
      setScopeSiteDraft('');
    }
  }, [scopeDialogOpen]);
  
  // Transport badge: mTLS verified on edge ingress, HTTPS-only on hosted Traefik, warn on real mTLS gaps.
  const identityBadge = useMemo(() => {
    const badgeState = resolveIdentityTransportBadgeState(identityInfo);
    if (badgeState.kind === 'hidden') return null;

    if (badgeState.kind === 'verified') {
      return (
        <Tooltip title={`mTLS verified: ${badgeState.clientCn || 'Unknown'}`} arrow>
          <Chip
            icon={<SuccessIcon sx={{ fontSize: 16 }} />}
            label="Identity Verified"
            size="small"
            color="success"
            variant="filled"
            sx={{
              mr: 1,
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        </Tooltip>
      );
    }

    if (badgeState.kind === 'https_ok') {
      return (
        <Tooltip title="HTTPS connection (client certificate not required for browser access)" arrow>
          <Chip
            icon={<SuccessIcon sx={{ fontSize: 16 }} />}
            label="HTTPS"
            size="small"
            color="success"
            variant="filled"
            sx={{
              mr: 1,
              bgcolor: 'success.dark',
              color: 'common.white',
              borderColor: 'success.dark',
              '& .MuiChip-icon': { color: 'common.white' },
            }}
          />
        </Tooltip>
      );
    }

    if (badgeState.kind === 'mtls_failed') {
      return (
        <Tooltip title="HTTPS connection, but client certificate verification failed" arrow>
          <Chip
            icon={<ErrorIcon sx={{ fontSize: 16 }} />}
            label="mTLS Failed"
            size="small"
            color="error"
            variant="outlined"
            sx={{ mr: 1 }}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="HTTPS connection, but client certificate not verified" arrow>
        <Chip
          icon={<UnknownIcon sx={{ fontSize: 16 }} />}
          label="HTTPS"
          size="small"
          color="warning"
          variant="outlined"
          sx={{ mr: 1 }}
        />
      </Tooltip>
    );
  }, [identityInfo]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarRailToggle = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      persistSidebarCollapsedPreference(next);
      return next;
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleOpenPasswordDialog = () => {
    handleMenuClose();
    setPasswordChangeError(null);
    setPasswordChangeSuccess(null);
    setCurrentPassword('');
    setNextPassword('');
    setConfirmNextPassword('');
    setPasswordDialogOpen(true);
  };

  const resetMfaDialogState = () => {
    setMfaCurrentPassword('');
    setMfaOtpCode('');
    setMfaSecret('');
    setMfaProvisioningUri('');
    setMfaError(null);
    setMfaSuccess(null);
  };

  const loadMfaStatus = async () => {
    try {
      setMfaStatusLoading(true);
      const response = await fetchOwnMfaStatus();
      setMfaEnabled(Boolean(response.mfa?.enabled));
      setMfaPendingSetup(Boolean(response.mfa?.pending_setup));
    } catch (err: any) {
      setMfaError(err?.response?.data?.error || err?.message || 'Unable to load MFA status.');
    } finally {
      setMfaStatusLoading(false);
    }
  };

  const handleOpenMfaDialog = async () => {
    handleMenuClose();
    resetMfaDialogState();
    setMfaEnabled(false);
    setMfaPendingSetup(false);
    setMfaDialogOpen(true);
    await loadMfaStatus();
  };

  const handleStartMfaSetup = async () => {
    if (!mfaCurrentPassword) {
      setMfaError('Current password is required.');
      return;
    }
    try {
      setMfaSubmitting(true);
      setMfaError(null);
      const response = await startOwnMfaSetup({ current_password: mfaCurrentPassword });
      setMfaEnabled(Boolean(response.mfa?.enabled));
      setMfaPendingSetup(Boolean(response.mfa?.pending_setup));
      setMfaSecret(String(response.setup?.secret || ''));
      setMfaProvisioningUri(String(response.setup?.provisioning_uri || ''));
      setMfaSuccess('Authenticator secret generated. Add it to your app, then verify with a code.');
    } catch (err: any) {
      setMfaError(err?.response?.data?.error || err?.message || 'MFA setup failed.');
    } finally {
      setMfaSubmitting(false);
    }
  };

  const handleVerifyMfaSetup = async () => {
    if (!mfaOtpCode) {
      setMfaError('Authenticator code is required.');
      return;
    }
    try {
      setMfaSubmitting(true);
      setMfaError(null);
      const response = await verifyOwnMfaSetup({ otp_code: mfaOtpCode });
      setMfaEnabled(Boolean(response.mfa?.enabled));
      setMfaPendingSetup(Boolean(response.mfa?.pending_setup));
      setMfaSecret('');
      setMfaProvisioningUri('');
      setMfaOtpCode('');
      setMfaSuccess('Multi-factor authentication enabled.');
    } catch (err: any) {
      setMfaError(err?.response?.data?.error || err?.message || 'MFA verification failed.');
    } finally {
      setMfaSubmitting(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!mfaCurrentPassword || !mfaOtpCode) {
      setMfaError('Current password and authenticator code are required.');
      return;
    }
    try {
      setMfaSubmitting(true);
      setMfaError(null);
      const response = await disableOwnMfa({
        current_password: mfaCurrentPassword,
        otp_code: mfaOtpCode,
      });
      setMfaEnabled(Boolean(response.mfa?.enabled));
      setMfaPendingSetup(Boolean(response.mfa?.pending_setup));
      resetMfaDialogState();
      setMfaSuccess('Multi-factor authentication disabled.');
    } catch (err: any) {
      setMfaError(err?.response?.data?.error || err?.message || 'MFA disable failed.');
    } finally {
      setMfaSubmitting(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !nextPassword) {
      setPasswordChangeError('Current and new password are required.');
      return;
    }
    if (nextPassword !== confirmNextPassword) {
      setPasswordChangeError('New passwords do not match.');
      return;
    }
    try {
      setPasswordSubmitting(true);
      setPasswordChangeError(null);
      await changeOwnPassword({
        current_password: currentPassword,
        new_password: nextPassword,
      });
      setPasswordChangeSuccess('Password changed.');
      setCurrentPassword('');
      setNextPassword('');
      setConfirmNextPassword('');
    } catch (err: any) {
      setPasswordChangeError(err?.response?.data?.error || err?.message || 'Password change failed');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const currentDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  const renderDrawer = (collapsed: boolean, showRailToggle = false) => (
    <Box
      data-testid={showRailToggle ? 'app-sidebar' : undefined}
      data-collapsed={showRailToggle ? (collapsed ? 'true' : 'false') : undefined}
    >
      <Toolbar 
        sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          flexDirection: 'column',
          alignItems: collapsed ? 'center' : 'flex-start',
          py: collapsed ? 1 : 2,
          px: collapsed ? 0.5 : 2,
          minHeight: collapsed ? 56 : 80,
        }}
      >
        {collapsed ? null : (
          <>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
              Edgentik
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, mt: 0.5 }}>
              Overview · Investigate · Controls
            </Typography>
          </>
        )}
        {showRailToggle ? (
          <Tooltip title={collapsed ? 'Show navigation' : 'Hide navigation'}>
            <IconButton
              color="inherit"
              size="small"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={handleSidebarRailToggle}
              data-testid="app-sidebar-collapse-toggle"
              sx={{
                mt: collapsed ? 0 : 0.75,
                alignSelf: collapsed ? 'center' : 'flex-end',
                bgcolor: 'rgba(255,255,255,0.18)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        ) : null}
      </Toolbar>
      <Divider />

      {NAV_MACRO_GROUPS.map((macroGroup) => {
        const groupedSections = warehouseSections.filter((section) => macroGroup.sectionKeys.includes(section.key));
        if (groupedSections.length === 0) return null;
        return (
          <React.Fragment key={`nav-macro-${macroGroup.label}`}>
            {collapsed ? null : (
            <ListSubheader
              sx={{
                lineHeight: '28px',
                fontWeight: 800,
                fontSize: '0.68rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                px: 2,
                pt: 2,
                pb: 0.75,
                mt: macroGroup.label === 'Operations' ? 0 : 0.5,
              }}
            >
              {macroGroup.label}
            </ListSubheader>
            )}
            {groupedSections.map((section, index) => {
        const macroIndex = index;
        const sectionActive = activeWarehouseSectionKey === section.key;
        const sectionHome = section.homePage;
        const sectionExpanded =
          laneSectionExpanded[section.key] === undefined ? sectionActive : Boolean(laneSectionExpanded[section.key]);
        const primaryItems = (section.primaryPages || []).map((page) => ({ text: page.text, path: page.path, icon: <page.icon /> }));
        const advancedItems = (section.advancedPages || []).map((page) => ({ text: page.text, path: page.path, icon: <page.icon /> }));
        const advancedExpanded = Boolean(laneAdvancedOpen[section.key]) && sectionExpanded;
        const sectionRootPath = sectionHome?.path || primaryItems[0]?.path;
        const sectionRootIcon = sectionHome
          ? <sectionHome.icon />
          : section.key === 'access'
            ? <AdminPanelSettingsIcon />
            : primaryItems[0]?.icon;
        const hasExpandableChildren = primaryItems.length > 0 || advancedItems.length > 0;
        // Compare full nav target (pathname + scoped query), not pathname alone.
        // Otherwise Review stays stuck on /incidents?incident_id=...&review=1 deep links,
        // and nested routes like /acceptance/proof never return to the lane home.
        const sectionHomeTarget = sectionHome ? buildNavPath(sectionHome.path) : '';
        const currentFullPath = `${location.pathname}${location.search || ''}`;
        const alreadyAtSectionHome = Boolean(sectionHome) && sectionHomeTarget === currentFullPath;
        const toggleSectionExpanded = () => {
          if (!hasExpandableChildren) return;
          setLaneSectionExpanded((prev) => ({
            ...prev,
            [section.key]: !sectionExpanded,
          }));
        };
        const handleSectionRowClick = () => {
          if (sectionHome && !alreadyAtSectionHome) {
            if (hasExpandableChildren) {
              setLaneSectionExpanded((prev) => ({ ...prev, [section.key]: true }));
            }
            handleMenuClick(sectionHome.path);
            return;
          }
          toggleSectionExpanded();
        };
        const handleSectionChevronClick = (event: React.MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          toggleSectionExpanded();
        };

        return (
          <React.Fragment key={section.key}>
            {sectionRootPath ? (
              <List dense disablePadding sx={{ pt: macroIndex === 0 ? 0.75 : 1, pb: hasExpandableChildren && sectionExpanded ? 0.25 : 0.75 }}>
                <ListItem
                  disablePadding
                  secondaryAction={
                    !collapsed && hasExpandableChildren ? (
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={sectionExpanded ? `Collapse ${section.label}` : `Expand ${section.label}`}
                        aria-expanded={sectionExpanded}
                        onClick={handleSectionChevronClick}
                        sx={{
                          mr: 0.75,
                          color: sectionActive ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        {sectionExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    ) : undefined
                  }
                  sx={{ pr: !collapsed && hasExpandableChildren ? 4.5 : undefined }}
                >
                  <Tooltip title={collapsed ? section.label : ''} placement="right" disableHoverListener={!collapsed}>
                    <ListItemButton
                      selected={sectionActive}
                      onClick={handleSectionRowClick}
                      aria-label={collapsed ? section.label : undefined}
                      sx={{
                        borderRadius: 1.5,
                        mx: collapsed ? 0.5 : 1,
                        py: 0.5,
                        justifyContent: collapsed ? 'center' : undefined,
                        px: collapsed ? 1 : undefined,
                        pr: !collapsed && hasExpandableChildren ? 4 : 1,
                        '&.Mui-selected': {
                          bgcolor: 'rgba(25, 118, 210, 0.12)',
                          color: 'primary.main',
                          '& .MuiListItemIcon-root': { color: 'primary.main' },
                          '& .MuiListItemText-primary': { fontWeight: 700 },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>{sectionRootIcon}</ListItemIcon>
                      {collapsed ? null : (
                        <ListItemText primary={section.label} primaryTypographyProps={{ variant: 'body1', fontWeight: 700 }} />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              </List>
            ) : null}
            <Collapse in={!collapsed && sectionExpanded && hasExpandableChildren} timeout="auto" unmountOnExit>
              <List dense disablePadding sx={{ pb: 0.5 }}>
                {primaryItems.map((item) => (
                  <ListItem key={`${section.key}-${item.path}`} disablePadding>
                    <Tooltip title={collapsed ? item.text : ''} placement="right" disableHoverListener={!collapsed}>
                      <ListItemButton
                        selected={isMenuSelected(item.path)}
                        onClick={() => handleMenuClick(item.path)}
                        aria-label={collapsed ? item.text : undefined}
                        sx={{
                          pl: collapsed ? 1 : 4,
                          borderRadius: 1.25,
                          mx: collapsed ? 0.5 : 1,
                          py: 0.25,
                          justifyContent: collapsed ? 'center' : undefined,
                          opacity: 0.96,
                          '&.Mui-selected': {
                            bgcolor: 'rgba(25, 118, 210, 0.12)',
                            color: 'primary.main',
                            '& .MuiListItemIcon-root': { color: 'primary.main' },
                            '& .MuiListItemText-primary': { fontWeight: 700 },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                        {collapsed ? null : (
                          <ListItemText primary={item.text} primaryTypographyProps={{ variant: 'body2' }} />
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                ))}
                {advancedItems.length > 0 && (
                  <>
                    <ListItem disablePadding>
                      <Tooltip title={collapsed ? 'Advanced tools' : ''} placement="right" disableHoverListener={!collapsed}>
                        <ListItemButton
                          onClick={() =>
                            setLaneAdvancedOpen((o) => ({
                              ...o,
                              [section.key]: !o[section.key],
                            }))
                          }
                          aria-label={collapsed ? 'Advanced tools' : undefined}
                          sx={{
                            borderRadius: 1.25,
                            mx: collapsed ? 0.5 : 1,
                            justifyContent: collapsed ? 'center' : undefined,
                            opacity: 0.8,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, justifyContent: 'center' }}>{advancedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</ListItemIcon>
                          {collapsed ? null : (
                            <ListItemText primary="Advanced tools" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', fontWeight: 500 }} />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                    <Collapse in={advancedExpanded} timeout="auto" unmountOnExit>
                      <List dense disablePadding>
                        {advancedItems.map((item) => (
                          <ListItem key={`${section.key}-${item.path}`} disablePadding>
                            <Tooltip title={collapsed ? item.text : ''} placement="right" disableHoverListener={!collapsed}>
                              <ListItemButton
                                selected={isMenuSelected(item.path)}
                                onClick={() => handleMenuClick(item.path)}
                                aria-label={collapsed ? item.text : undefined}
                                sx={{
                                  pl: collapsed ? 1 : 5,
                                  borderRadius: 1,
                                  mx: collapsed ? 0.5 : 1,
                                  justifyContent: collapsed ? 'center' : undefined,
                                  opacity: 0.86,
                                  '&.Mui-selected': {
                                    bgcolor: 'rgba(25, 118, 210, 0.12)',
                                    color: 'primary.main',
                                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                                    '& .MuiListItemText-primary': { fontWeight: 700 },
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 34, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                                {collapsed ? null : (
                                  <ListItemText primary={item.text} primaryTypographyProps={{ variant: 'body2' }} />
                                )}
                              </ListItemButton>
                            </Tooltip>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </>
                )}
              </List>
            </Collapse>
            <Divider sx={{ my: 1.25, mx: 1.5, opacity: 0.7 }} />
          </React.Fragment>
        );
            })}
          </React.Fragment>
        );
      })}
    </Box>
  );

  const externalReviewerCaps = getCapabilitiesFromUser(effectiveUser);
  if (
    isExternalSafetyReviewer(effectiveUser) &&
    !isProductEvaluator(effectiveUser) &&
    !hasCapability(externalReviewerCaps, 'incidents:read')
  ) {
    return <Navigate to="/review" replace />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Edgentik – Warehouse
          </Typography>
          {siteChip}
          {identityBadge}
          {hasControlPlaneApiKey && !user && (
            <Tooltip
              title="Using control-plane API key (no login). If you expect RBAC features, log in or clear stale auth."
              arrow
            >
              <Chip
                label="API key"
                size="small"
                variant="filled"
                sx={{
                  mr: 1,
                  bgcolor: 'rgba(255,255,255,0.18)',
                  color: 'common.white',
                  borderColor: 'rgba(255,255,255,0.25)',
                }}
              />
            </Tooltip>
          )}
          {authNote && (
            <Tooltip title={authNote} arrow>
              <Chip
                label="Auth reset"
                size="small"
                color="warning"
                variant="filled"
                onDelete={() => setAuthNote(null)}
                sx={{ mr: 1 }}
              />
            </Tooltip>
          )}
          {incidentHealthChip}
          {liveViewHealthChip}
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleMenuClose}>
              <Avatar sx={{ width: 24, height: 24, mr: 1 }} />
              {user?.username}
            </MenuItem>
            <Divider />
            {canShowPersonaPreview && (
              <>
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 700 }}>Preview as persona (UX only)</ListSubheader>
                {PERSONA_PREVIEW_GROUPS.map((group) => {
                  const presets = PERSONA_PRESETS.filter((preset) => group.presetIds.includes(preset.id));
                  if (!presets.length) return null;
                  return (
                    <React.Fragment key={group.label}>
                      <ListSubheader sx={{ lineHeight: '28px', color: 'text.secondary' }}>{group.label}</ListSubheader>
                      {presets.map((p) => (
                        <MenuItem
                          key={p.id}
                          selected={previewPersonaId === p.id}
                          onClick={() => {
                            if (!user) return;
                            setPreviewPersona(p.id);
                            handleMenuClose();
                            const nextEffective = { ...user, capabilities: [...p.capabilities] };
                            navigate(resolveWarehouseLandingPath(nextEffective, readNavContext(), p.id));
                          }}
                        >
                          {p.label}
                        </MenuItem>
                      ))}
                    </React.Fragment>
                  );
                })}
                {previewPersonaId ? (
                  <MenuItem
                    onClick={() => {
                      clearPreviewPersona();
                      handleMenuClose();
                      navigate('/');
                    }}
                  >
                    Back to real permissions
                  </MenuItem>
                ) : null}
                <Divider />
              </>
            )}
            <MenuItem onClick={handleOpenPasswordDialog}>
              <PasswordIcon sx={{ mr: 1 }} />
              Change Password
            </MenuItem>
            <MenuItem onClick={handleOpenMfaDialog}>
              <MfaIcon sx={{ mr: 1 }} />
              Manage MFA
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: `${currentDrawerWidth}px` }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {renderDrawer(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: `${currentDrawerWidth}px`,
              overflowX: 'hidden',
              transition: (theme) => theme.transitions.create('width', {
                duration: theme.transitions.duration.shorter,
              }),
            },
          }}
          open
        >
          {renderDrawer(sidebarCollapsed, true)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: ['/grafana', '/odoo', '/live'].includes(location.pathname) ? 0 : 3,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
        }}
      >
        <Toolbar />
        {previewPersonaId && getPersonaPresetById(previewPersonaId) && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => { clearPreviewPersona(); navigate('/'); }}>
                Exit preview
              </Button>
            }
          >
            Preview mode: <strong>{getPersonaPresetById(previewPersonaId)?.label}</strong>. Frontend visibility only;
            backend permissions unchanged.
          </Alert>
        )}
        {isSandboxEhsEvaluator(effectiveUser) ? (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="worksafebc-sandbox-banner">
            Controlled evaluation sandbox. No production employer data.
          </Alert>
        ) : null}
        <RouteRenderBoundary resetKey={`${location.pathname}${location.search || ''}`}>
          <ProductEvaluatorScopeBootstrap />
          <Outlet />
        </RouteRenderBoundary>
      </Box>

      <Dialog open={scopeDialogOpen} onClose={() => setScopeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set org/site scope</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This controls which site's data you see (zones, cameras, incidents, etc.). Change this to switch between sites like yyz, default, etc.
          </Alert>
          <TextField
            fullWidth
            label="org_id"
            value={scopeOrgDraft}
            onChange={(e) => setScopeOrgDraft(e.target.value)}
            margin="dense"
            placeholder="e.g. default, acme"
          />
          <TextField
            fullWidth
            label="site_id"
            value={scopeSiteDraft}
            onChange={(e) => setScopeSiteDraft(e.target.value)}
            margin="dense"
            placeholder="e.g. default, yyz"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScopeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const org = String(scopeOrgDraft || '').trim() || 'default';
              const site = String(scopeSiteDraft || '').trim() || 'default';
              persistUiScope(org, site, { userSet: true });
              setScopeDialogOpen(false);
              setScopeNonce((n) => n + 1);
              window.dispatchEvent(new CustomEvent('edgentik:scope-changed', { detail: { orgId: org, siteId: site } }));
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {passwordChangeError && <Alert severity="error">{passwordChangeError}</Alert>}
            {passwordChangeSuccess && <Alert severity="success">{passwordChangeSuccess}</Alert>}
            <TextField
              fullWidth
              label="Current password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <TextField
              fullWidth
              label="New password"
              type="password"
              value={nextPassword}
              onChange={(e) => setNextPassword(e.target.value)}
            />
            <TextField
              fullWidth
              label="Confirm new password"
              type="password"
              value={confirmNextPassword}
              onChange={(e) => setConfirmNextPassword(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={passwordSubmitting}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mfaDialogOpen} onClose={() => setMfaDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage multi-factor authentication</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {mfaError && <Alert severity="error">{mfaError}</Alert>}
            {mfaSuccess && <Alert severity="success">{mfaSuccess}</Alert>}
            {mfaStatusLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Alert severity={mfaEnabled ? 'success' : 'info'}>
                  {mfaEnabled
                    ? 'MFA is enabled for your account.'
                    : 'MFA is not enabled. Add a TOTP authenticator app to enable it.'}
                </Alert>
                <TextField
                  fullWidth
                  label="Current password"
                  type="password"
                  value={mfaCurrentPassword}
                  onChange={(e) => setMfaCurrentPassword(e.target.value)}
                />
                {!mfaEnabled && (
                  <>
                    <Button variant="outlined" onClick={handleStartMfaSetup} disabled={mfaSubmitting}>
                      Generate MFA setup
                    </Button>
                    {(mfaPendingSetup || mfaSecret || mfaProvisioningUri) && (
                      <>
                        <TextField
                          fullWidth
                          label="Authenticator secret"
                          value={mfaSecret}
                          InputProps={{ readOnly: true }}
                        />
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Provisioning URI"
                          value={mfaProvisioningUri}
                          InputProps={{ readOnly: true }}
                        />
                        <TextField
                          fullWidth
                          label="Authenticator code"
                          value={mfaOtpCode}
                          onChange={(e) => setMfaOtpCode(e.target.value)}
                        />
                        <Button variant="contained" onClick={handleVerifyMfaSetup} disabled={mfaSubmitting}>
                          Enable MFA
                        </Button>
                      </>
                    )}
                  </>
                )}
                {mfaEnabled && (
                  <>
                    <TextField
                      fullWidth
                      label="Authenticator code"
                      value={mfaOtpCode}
                      onChange={(e) => setMfaOtpCode(e.target.value)}
                    />
                    <Button color="warning" variant="contained" onClick={handleDisableMfa} disabled={mfaSubmitting}>
                      Disable MFA
                    </Button>
                  </>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMfaDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;
